// https://www.notion.so/prextech/VARA-alert-with-en-AE-locale-25beb32ad227806dad6ef4cec2fa9e08?v=1b4eb32ad2278041b2fd000c16680123

import { useMutation } from '@tanstack/react-query';
import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import { postUserLangApi } from '@flipster/apis/v1/user';
import { authBunja, useAuth } from '@flipster/features/src/shared/auth/state';
import { userRegulationBunja } from '@flipster/features/src/shared/regulation';
import { useIsWebview } from '@flipster/features/src/shared/webview';
import { useSupportedLocale } from '@flipster/i18n/hooks';
import { usePathname, useRouter } from '@flipster/i18n/routing';
import { useOverlay } from '@flipster/overlay';
import { Dialog, dialog } from '@flipster/react/design-system/dialog';
import { snackbar } from '@flipster/react/design-system/snackbar/index';
import { IsomorphicLink } from '@flipster/react/design-utils/isomorphic-link';
import { css } from '@flipster/styled-system/css';
import { deleteCookieEverywhere } from '@flipster/utils/consent/utils';
import { cookieStore } from '@flipster/utils/cookie';
import segment from '@flipster/utils/segment';
import { VaraEvent } from '@flipster/utils/segment/events/vara';

import { SignInPath } from '../../proxy';

type VaraAlertType = 'tempOpen' | 'varaLocation' | 'localeChangeGuide' | 'languageSetting' | 'restrictedService';

const SpotOnlyWhitelistPatterns = [
  /^\/$/,
  /\/(about|assets|fullchart|pnl-analysis|policies|blog|auth|glossary|travel-rule)/,
  /\/(trade|market|history)\/spot/,
  /\/transaction\/(conversion|deposit|withdrawal)/,
  /\/support\/(guide|spot-pairs|proof-of-reserves|available-asset|zendesk)/,
  /\/faq\/(withdrawal|deposit)/,
  /\/user\/(account|kyc)/,
] as const;

// You can see the details of the alert cases in the document at the top of the file.
const VaraAlertConfig = {
  tempOpen: {
    title: 'Coming soon to your region',
    message: (
      <>
        <p>Flipster isn't available in your region yet, but we're working on it. You can still explore the platform.</p>
        <br />
        <p>
          Need help? Please reach out to{' '}
          <IsomorphicLink
            href='mailto:support@flipster.io'
            target='_blank'
            rel='noreferrer'
            className={css({ textStyle: 'Text/M/Underline', color: 'content.default.level-2' })}
          >
            support@flipster.io
          </IsomorphicLink>
          .
        </p>
      </>
    ),
    confirmText: 'Continue',
  },
  varaLocation: {
    title: 'Access Notice',
    message: 'Your access may be subject to UAE regulations. Please visit our UAE site for the best experience.',
    confirmText: 'Go to Flipster in UAE',
  },
  localeChangeGuide: {
    title: 'Access Notice',
    message:
      'Your access may be subject to non-UAE settings. Please visit our international English site for the best experience.',
    confirmText: 'Go to Flipster International',
  },
  languageSetting: {
    title: 'Language setting alert',
    message:
      "This service isn't available in your current language setting. Please switch to a supported language for the best experience.",
    confirmText: 'Close',
  },
  restrictedService: {
    title: 'Restricted service',
    message:
      'We regret to inform you that some of Flipster services are not currently available in your location. But many of our core services remain available. You can continue enjoying Flipster by accessing our supported site in the UAE.',
    confirmText: 'Go to Flipster in UAE',
  },
} as const;

const getRedirectPath = ({
  pathname,
  isMainPage,
  isCookieLoggedIn,
  isWhitelistPage,
}: {
  pathname: string;
  isMainPage: boolean;
  isCookieLoggedIn: boolean;
  isWhitelistPage: boolean;
}) => {
  if (pathname === '/market/perpetual') return '/market/spot';
  if (pathname.startsWith('/trade/perpetual')) return '/trade/spot/VARA_BTCUSDT';
  if (!isMainPage && isCookieLoggedIn) return '/assets';
  if (!isWhitelistPage) return '/';
  return pathname;
};

export const useVARARegulationPopup = () => {
  const overlay = useOverlay();
  const overlayRef = useRef(overlay);
  overlayRef.current = overlay;
  const isWebview = useIsWebview();

  const pathname = usePathname();
  const router = useRouter();
  const locale = useSupportedLocale();

  const { getMe, user } = useAuth();

  const { mutateAsync: updateUserLanguage } = useMutation({
    mutationKey: ['postUserLangApi'],
    mutationFn: postUserLangApi,
    onSuccess: async () => {
      await getMe();
    },
    onError: () => {
      snackbar.error({ message: 'Fail to update user language' });
      window.location.reload();
    },
  });

  const { isCookieLoggedInAtom } = useBunja(authBunja);
  const { isVaraTemporaryOpenAtom, isVaraRegulatedAtom, isRegulationLoadingAtom } = useBunja(userRegulationBunja);

  const isCookieLoggedIn = useAtomValue(isCookieLoggedInAtom);
  const isVaraTemporaryOpen = useAtomValue(isVaraTemporaryOpenAtom);
  const isVaraRegulated = useAtomValue(isVaraRegulatedAtom);
  const isRegulationLoading = useAtomValue(isRegulationLoadingAtom);

  const isUAELocale = locale === 'en-AE';
  // NEXT_LOCALE 쿠키 sticky 등으로 미들웨어가 일시적으로 /en-AE 로 튕긴 상태
  const isTransientUAELocale = isUAELocale && !!user?.redirectLocale && user.redirectLocale !== 'en-AE';
  const isMainPage = pathname === '/';
  const isWhitelistPage = useMemo(
    () =>
      SignInPath.some((path) => pathname.startsWith(path)) ||
      SpotOnlyWhitelistPatterns.some((filter) => pathname.match(filter)),
    [pathname],
  );

  const redirectToSpotOnly = useCallback(() => {
    const redirectPath = getRedirectPath({ pathname, isMainPage, isCookieLoggedIn, isWhitelistPage });
    const targetUrl = isVaraRegulated && !isUAELocale ? `/en-AE${redirectPath}` : redirectPath;

    if (isVaraRegulated && !isUAELocale) {
      window.location.replace(targetUrl);
    } else {
      router.replace(targetUrl);
    }
  }, [pathname, isMainPage, isCookieLoggedIn, isVaraRegulated, isUAELocale, isWhitelistPage, router]);

  const activeAlertType = useMemo((): VaraAlertType | null => {
    if (isWebview) return null;
    if (isRegulationLoading) return null;

    if (isCookieLoggedIn) {
      // User logged in cases
      if (isVaraRegulated && isWhitelistPage && isVaraTemporaryOpen && isMainPage) return 'tempOpen';
      if (isVaraRegulated && !isUAELocale && isWhitelistPage) return 'varaLocation';
      if (!isVaraRegulated && isUAELocale && isWhitelistPage && !isTransientUAELocale) return 'localeChangeGuide';
      if (!isVaraRegulated && isUAELocale && !isWhitelistPage && !isTransientUAELocale) return 'languageSetting';
      if (isVaraRegulated && !isWhitelistPage) return 'restrictedService';
    } else {
      // User not logged in cases
      if (isVaraTemporaryOpen && isMainPage && isVaraRegulated && isWhitelistPage) return 'tempOpen';
      if (isVaraRegulated && !isUAELocale && isWhitelistPage && !(isVaraTemporaryOpen && isMainPage))
        return 'varaLocation';
      if (!isVaraRegulated && isUAELocale && isWhitelistPage) return 'localeChangeGuide';
      if (!isVaraRegulated && isUAELocale && !isWhitelistPage) return 'languageSetting';
      if (!isWhitelistPage && isVaraRegulated) return 'restrictedService';
    }

    return null;
  }, [
    isWebview,
    isRegulationLoading,
    isCookieLoggedIn,
    isVaraRegulated,
    isUAELocale,
    isTransientUAELocale,
    isWhitelistPage,
    isVaraTemporaryOpen,
    isMainPage,
  ]);

  const handleTempOpen = useCallback(() => {
    if (isVaraRegulated && !isUAELocale) redirectToSpotOnly();
  }, [isVaraRegulated, isUAELocale, redirectToSpotOnly]);

  const handleVaraLocation = useCallback(async () => {
    if (isCookieLoggedIn) {
      await updateUserLanguage('en-AE');
    }
    redirectToSpotOnly();
  }, [isCookieLoggedIn, redirectToSpotOnly, updateUserLanguage]);

  const handleLocaleChangeGuide = useCallback(async () => {
    if (isCookieLoggedIn) {
      await updateUserLanguage('en-US');
    }
    deleteCookieEverywhere('NEXT_LOCALE');
    cookieStore.update('NEXT_LOCALE', 'en', 365);
    window.location.replace(`/en${pathname}`);
  }, [isCookieLoggedIn, pathname, updateUserLanguage]);

  const handleRestrictedService = useCallback(async () => {
    redirectToSpotOnly();
    if (isCookieLoggedIn && !isUAELocale) {
      await updateUserLanguage('en-AE');
    }
  }, [isCookieLoggedIn, isUAELocale, redirectToSpotOnly, updateUserLanguage]);

  const actionMap = useMemo(
    (): Partial<Record<VaraAlertType, () => void>> => ({
      tempOpen: handleTempOpen,
      varaLocation: handleVaraLocation,
      localeChangeGuide: handleLocaleChangeGuide,
      languageSetting: redirectToSpotOnly,
      restrictedService: handleRestrictedService,
    }),
    [handleTempOpen, handleVaraLocation, handleLocaleChangeGuide, redirectToSpotOnly, handleRestrictedService],
  );

  const prevAlertTypeRef = useRef<VaraAlertType | null>(null);

  // pathname이 변경되면 prevAlertTypeRef 리셋 — 다른 페이지로 이동 시 조건에 해당하면 다시 노출
  useEffect(() => {
    prevAlertTypeRef.current = null;
  }, [pathname]);

  useEffect(() => {
    if (!activeAlertType) return;
    // 동일 페이지에서 리렌더로 인한 중복 오픈 방지
    if (activeAlertType === prevAlertTypeRef.current) {
      return;
    }

    prevAlertTypeRef.current = activeAlertType;

    overlayRef.current.closeSlot('modal');
    const notice = { ...VaraAlertConfig[activeAlertType], onConfirm: actionMap[activeAlertType] };
    dialog.open({
      title: { label: notice.title },
      content: notice.message,
      buttons: {
        primary: <Dialog.Button hierarchy='primary' label={notice.confirmText} onClick={notice.onConfirm} />,
      },
    });
    segment.track(VaraEvent.VaraRestrictionModalView);
  }, [activeAlertType, actionMap]);
};
